import { useEffect, useState } from "react";
import { Download, Copy, Share2, Check, BarChart3, Video, Captions } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/share/authority-checklist`;
export const SHARE_URL = BASE;
export const shareUrl = (channel) => `${BASE}?c=${channel}`;
export const SHARE_CAPTION =
  "Just applied for your MC? Free New Authority Checklist — every filing, permit and account from MC# to first load, in order, with costs and official links. Tick it off from your phone:";

export function AuthorityShareCard({ user }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_CAPTION} ${shareUrl("copy")}`);
      setCopied(true);
      toast.success("Caption + link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press the text instead"); }
  };

  const native = async () => {
    if (!navigator.share) return copy();
    try { await navigator.share({ title: "New Authority Checklist", text: SHARE_CAPTION, url: shareUrl("native") }); } catch { /* cancelled */ }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors";
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="authority-share-card">
      <div className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-5 items-center">
        <img
          src="/authority-checklist-share.png"
          alt="New Authority Checklist — FreightPilot AI share card"
          className="rounded-xl border border-white/10 w-full"
          data-testid="authority-share-image"
        />
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">Drop it in a trucking group</div>
          <p className="text-sm text-white/70 leading-relaxed mb-4">
            Paste the link in TikTok comments or a Facebook group and it unfurls with this card — looks like a real download, not a bare URL.
          </p>
          <p className="text-xs text-white/50 leading-relaxed mb-4 border-l-2 border-white/10 pl-3" data-testid="authority-share-caption">"{SHARE_CAPTION}"</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={copy} className={`${btn} bg-[#FF5A1F] text-black hover:bg-[#FF7038]`} data-testid="authority-share-copy">
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy caption + link</>}
            </button>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl("facebook"))}&quote=${encodeURIComponent(SHARE_CAPTION)}`}
              target="_blank" rel="noreferrer"
              className={`${btn} border border-white/15 text-white/80 hover:border-[#1877F2] hover:text-[#1877F2]`}
              data-testid="authority-share-facebook"
            >
              Share to Facebook
            </a>
            <a href="/authority-checklist-share.png" download="freightpilot-new-authority-checklist.png" className={`${btn} border border-white/15 text-white/80 hover:border-white/40`} data-testid="authority-share-download">
              <Download className="w-3.5 h-3.5" /> Download card
            </a>
            <button type="button" onClick={native} className={`${btn} border border-white/15 text-white/80 hover:border-white/40`} data-testid="authority-share-native">
              <Share2 className="w-3.5 h-3.5" /> Share…
            </button>
          </div>
        </div>
      </div>
      {user?.is_admin && <ShareStats />}
      <CommentReplies />
    </div>
  );
}

const SHORT_LINK = "freightpilotai.ai/mc";
const REPLIES = [
  {
    text: `Free step-by-step: ${SHORT_LINK} — BOC-3, UCR, IRP, IFTA, insurance, DAT in order with costs. Tick it off from your phone 🚛`,
    script: {
      hook: "Getting your MC and don't know what comes first?",
      body: "There are 25 steps — BOC-3, UCR, IRP plates, IFTA, insurance filings, DAT — and the order matters. Skip one and your authority never goes active.",
      cta: `I put the whole thing in a free checklist — ${SHORT_LINK}. Tick it off from your phone.`,
      onScreen: "25 STEPS · IN ORDER · FREE",
    },
  },
  {
    text: `Apply on FMCSA (~2 wks for the #), then BOC-3 + insurance filings same day, 21-day wait → ACTIVE. Full checklist: ${SHORT_LINK}`,
    script: {
      hook: "Here's the real MC timeline nobody tells you.",
      body: "Apply on FMCSA — about two weeks to get your number. The day it posts: BOC-3, insurance filings, UCR. Then a 21-day protest window before you're ACTIVE.",
      cta: `Every step with costs and links: ${SHORT_LINK}. Free.`,
      onScreen: "2 WKS → NUMBER · 21 DAYS → ACTIVE",
    },
  },
  {
    text: `Biggest mistakes: skipping the Clearinghouse + paying "DOT fee" scam mailers. Free 25-step checklist: ${SHORT_LINK}`,
    script: {
      hook: "Two mistakes that cost new owner-ops hundreds.",
      body: "One: skipping the Drug & Alcohol Clearinghouse — the new-entrant audit checks it first. Two: paying those 'DOT compliance fee' letters. FMCSA never mails you a bill.",
      cta: `The free checklist flags every trap — ${SHORT_LINK}.`,
      onScreen: "FMCSA NEVER MAILS A BILL",
    },
  },
  {
    text: `Budget insurance first — $1M liability + $100k cargo, quotes swing thousands. Whole MC→first-load plan free: ${SHORT_LINK}`,
    script: {
      hook: "Before you buy the truck, price the insurance.",
      body: "Brokers want a million liability and a hundred thousand cargo. New-venture quotes swing thousands a year between carriers — get at least three.",
      cta: `Quote tracker plus the whole MC-to-first-load plan, free: ${SHORT_LINK}.`,
      onScreen: "$1M LIABILITY · $100K CARGO · 3 QUOTES",
    },
  },
];
const scriptText = (s) => `${s.hook}\n\n${s.body}\n\n${s.cta}\n\n[On-screen: ${s.onScreen}]`;
const wordCount = (s) => `${s.hook} ${s.body} ${s.cta}`.trim().split(/\s+/).length;

// Auto-captions: split each segment into ≤5-word chunks spread evenly across its time window.
const WINDOWS = [["hook", 0, 3], ["body", 3, 11], ["cta", 11, 15]];
const toCaptions = (s) => {
  const out = [];
  WINDOWS.forEach(([key, start, end]) => {
    const words = s[key].split(/\s+/).filter(Boolean);
    const chunks = [];
    for (let i = 0; i < words.length; i += 5) chunks.push(words.slice(i, i + 5).join(" "));
    const step = (end - start) / chunks.length;
    chunks.forEach((text, i) => out.push({ from: start + i * step, to: start + (i + 1) * step, text }));
  });
  return out;
};
const srtTime = (t) => {
  const ms = Math.round(t * 1000);
  const h = String(Math.floor(ms / 3600000)).padStart(2, "0");
  const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, "0");
  const sec = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");
  return `${h}:${m}:${sec},${String(ms % 1000).padStart(3, "0")}`;
};
const toSRT = (caps) => caps.map((c, i) => `${i + 1}\n${srtTime(c.from)} --> ${srtTime(c.to)}\n${c.text}`).join("\n\n") + "\n";
const fmtSec = (t) => `${t.toFixed(1)}s`;

function CaptionTrack({ script, idx, onCopy, copiedKey }) {
  const [open, setOpen] = useState(false);
  const caps = toCaptions(script);
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.08]" data-testid={`comment-reply-captions-${idx}`}>
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-white/60 hover:text-white" data-testid={`comment-reply-captions-toggle-${idx}`}>
          <Captions className="w-3.5 h-3.5 text-[#FF5A1F]" /> Auto-captions · {caps.length} lines {open ? "▾" : "▸"}
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={() => onCopy(caps.map((c) => c.text).join("\n"), `cap${idx}`, "Caption lines copied — paste as text overlays")} className="text-xs font-bold text-white/70 hover:text-white" data-testid={`comment-reply-captions-copy-${idx}`}>
            {copiedKey === `cap${idx}` ? "Copied" : "Copy lines"}
          </button>
          <button type="button" onClick={() => onCopy(toSRT(caps), `srt${idx}`, "SRT copied — import in CapCut / TikTok captions")} className="text-xs font-bold text-[#FF5A1F] hover:text-[#FF7038]" data-testid={`comment-reply-captions-srt-${idx}`}>
            {copiedKey === `srt${idx}` ? "Copied" : "Copy .srt"}
          </button>
        </div>
      </div>
      {open && (
        <ol className="mt-2 space-y-1 text-xs" data-testid={`comment-reply-captions-list-${idx}`}>
          {caps.map((c, i) => (
            <li key={i} className="flex gap-3">
              <span className="tabular-nums text-white/35 w-[92px] shrink-0">{fmtSec(c.from)} → {fmtSec(c.to)}</span>
              <span className="text-white/85 font-semibold">{c.text}</span>
            </li>
          ))}
        </ol>
      )}
      <p className="text-[11px] text-white/35 mt-1.5">Reads without sound in the feed. Big white text, black outline, bottom third — or paste the .srt into CapCut.</p>
    </div>
  );
}

function CommentReplies() {
  const [copiedKey, setCopiedKey] = useState("");
  const [openScript, setOpenScript] = useState(-1);
  const copy = async (text, key, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(msg);
      setTimeout(() => setCopiedKey(""), 1800);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="mt-5 pt-5 border-t border-white/10" data-testid="authority-comment-replies">
      <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">TikTok comment replies · "how do I get my MC?"</div>
      <p className="text-xs text-white/45 mb-3">Each one fits TikTok's 150-character comment limit and uses the short link <b className="text-white/70">{SHORT_LINK}</b> (tracked as tiktok_comment). Tap <b className="text-white/70">Video script</b> for a 15-second version you can record as a video reply.</p>
      <ul className="space-y-2">
        {REPLIES.map((r, i) => (
          <li key={i} className="rounded-xl border border-white/10 bg-black/20 px-3.5 py-2.5" data-testid={`comment-reply-${i}`}>
            <div className="flex items-start gap-3">
              <p className="flex-1 text-sm text-white/80 leading-relaxed">{r.text}</p>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => setOpenScript(openScript === i ? -1 : i)} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${openScript === i ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-white/15 hover:border-white/40"}`} data-testid={`comment-reply-script-toggle-${i}`}>
                    <Video className="w-3 h-3" /> Video script
                  </button>
                  <button type="button" onClick={() => copy(r.text, `c${i}`, "Reply copied — paste it under the comment")} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs font-bold hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors" data-testid={`comment-reply-copy-${i}`}>
                    {copiedKey === `c${i}` ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <span className={`text-[10px] tabular-nums ${r.text.length > 150 ? "text-red-400" : "text-white/35"}`} data-testid={`comment-reply-len-${i}`}>{r.text.length}/150</span>
              </div>
            </div>
            {openScript === i && (
              <div className="mt-3 rounded-lg border border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.05] p-3 text-sm" data-testid={`comment-reply-script-${i}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">15-second video reply · ~{wordCount(r.script)} words · read it at a normal pace</div>
                  <button type="button" onClick={() => copy(scriptText(r.script), `s${i}`, "Script copied — open TikTok, tap Reply with video")} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF5A1F] hover:text-[#FF7038]" data-testid={`comment-reply-script-copy-${i}`}>
                    {copiedKey === `s${i}` ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy script</>}
                  </button>
                </div>
                <p className="text-white leading-relaxed"><span className="text-white/40 text-[11px] uppercase tracking-wider mr-2">Hook 0–3s</span>{r.script.hook}</p>
                <p className="text-white/85 leading-relaxed mt-1.5"><span className="text-white/40 text-[11px] uppercase tracking-wider mr-2">Body 3–11s</span>{r.script.body}</p>
                <p className="text-white/85 leading-relaxed mt-1.5"><span className="text-white/40 text-[11px] uppercase tracking-wider mr-2">CTA 11–15s</span>{r.script.cta}</p>
                <p className="text-white/50 text-xs mt-2">On-screen text: <b className="text-white/80">{r.script.onScreen}</b> · Film vertical, point at the text when you say the link.</p>
                <CaptionTrack script={r.script} idx={i} onCopy={copy} copiedKey={copiedKey} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ShareStats() {
  const [card, setCard] = useState(null);
  useEffect(() => {
    api.get("/admin/share-attribution").then(({ data }) => setCard((data.cards || [])[0] || null)).catch(() => {});
  }, []);
  if (!card) return null;
  const stat = (label, value, testid) => (
    <div data-testid={testid}>
      <div className="text-xl font-black tabular-nums text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/45 font-bold">{label}</div>
    </div>
  );
  return (
    <div className="mt-5 pt-5 border-t border-white/10" data-testid="authority-share-stats">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">
        <BarChart3 className="w-3.5 h-3.5 text-[#FF5A1F]" /> Share link performance · founder view
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {stat("Visits", card.visits, "share-stat-visits")}
        {stat("Unique", card.unique_visitors, "share-stat-unique")}
        {stat("Last 7 days", card.visits_7d, "share-stat-visits-7d")}
        {stat("Signups", card.signups, "share-stat-signups")}
        {stat("Conversion", `${card.conversion_pct}%`, "share-stat-conversion")}
      </div>
      {card.channels?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 text-xs text-white/55" data-testid="share-stat-channels">
          {card.channels.map((c) => <span key={c.channel} className="rounded-full border border-white/10 px-2.5 py-0.5">{c.channel}: {c.visits}</span>)}
        </div>
      )}
    </div>
  );
}
